const breakfastFields = [
  'total_halves',
  'white_halves',
  'seeded_halves',
  'boiled_eggs',
  'fried_eggs',
  'scrambled_eggs',
];

const number = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeSelections = (selections) => {
  const normalized = (selections || [])
    .map((selection) => ({
      variant_id: selection.variant_id || null,
      count: number(selection.count),
    }))
    .filter((selection) => selection.count > 0)
    .sort((left, right) => String(left.variant_id).localeCompare(String(right.variant_id)));
  return normalized.length > 0 ? normalized : null;
};

export const existingVariantCounts = (existing, variants, mealType) => {
  const available = new Set((variants || []).map(variant => variant.id));
  const selections = normalizeSelections(existing?.[`${mealType}_variant_selections`]);
  if (selections) {
    return Object.fromEntries(
      selections
        .filter(selection => available.has(selection.variant_id))
        .map(selection => [selection.variant_id, selection.count]),
    );
  }

  const variantId = existing?.[`${mealType}_variant_id`];
  if (!available.has(variantId)) return {};
  return { [variantId]: number(existing?.[`${mealType}_count`], 1) || 1 };
};

const breakfastSelected = (item) => (
  breakfastFields.some((field) => number(item?.[field]) > 0)
  || Boolean(item?.has_coffee)
);

const breakfastState = (item, notes = '') => ({
  selected: breakfastSelected(item),
  breakfast: {
    total_halves: number(item?.total_halves),
    white_halves: number(item?.white_halves),
    seeded_halves: number(item?.seeded_halves),
    toppings: [...(item?.toppings || [])],
    without_butter: (item?.toppings || []).map((_, index) => Boolean(item?.without_butter?.[index])),
    boiled_eggs: number(item?.boiled_eggs),
    fried_eggs: number(item?.fried_eggs),
    scrambled_eggs: number(item?.scrambled_eggs),
    has_coffee: Boolean(item?.has_coffee),
  },
  notes: String(notes || ''),
});

const mealState = (source, mealType, topLevel = {}) => {
  const selected = Boolean(
    topLevel[`has_${mealType}`] ?? source?.[`has_${mealType}`],
  );
  const selections = normalizeSelections(
    topLevel[`${mealType}_variant_selections`]
      || source?.[`${mealType}_variant_selections`],
  );
  return {
    selected,
    variant_id: topLevel[`${mealType}_variant_id`]
      || source?.[`${mealType}_variant_id`]
      || null,
    count: number(
      topLevel[`${mealType}_count`] ?? source?.[`${mealType}_count`],
      1,
    ) || 1,
    variant_selections: selections,
  };
};

const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const buildMealEditPayload = (existingOrder, draftItem, mutationId) => {
  const existingItem = existingOrder?.breakfast_items?.[0] || {};
  const categories = {};
  const editable = (mealType) => (
    existingOrder?.meal_editability?.[mealType]?.editable !== false
  );

  const oldBreakfast = breakfastState(
    existingItem,
    existingOrder?.notes ?? existingItem.notes,
  );
  const newBreakfast = breakfastState(draftItem, draftItem?.notes);
  if (editable('breakfast') && !equal(oldBreakfast, newBreakfast)) {
    categories.breakfast = newBreakfast;
  }

  ['lunch', 'dinner'].forEach((mealType) => {
    const oldMeal = mealState(existingItem, mealType, existingOrder);
    const newMeal = mealState(draftItem, mealType);
    if (editable(mealType) && !equal(oldMeal, newMeal)) {
      categories[mealType] = newMeal;
    }
  });

  return {
    mutation_id: mutationId,
    expected_revision: number(existingOrder?.revision),
    categories,
  };
};

export const isButterOnlyEdit = (existingOrder, draftItem) => {
  if (!existingOrder || !draftItem) return false;
  const oldFlags = breakfastState(existingOrder.breakfast_items?.[0]).breakfast.without_butter;
  const newFlags = breakfastState(draftItem).breakfast.without_butter;
  return !equal(oldFlags, newFlags) && Object.keys(buildMealEditPayload(
    existingOrder, { ...draftItem, without_butter: oldFlags }, 'preview',
  ).categories).length === 0;
};

export const existingOrderFormData = (existingOrder) => {
  const item = existingOrder?.breakfast_items?.[0] || {};
  return {
    ...item,
    notes: existingOrder?.notes ?? item.notes ?? '',
    has_lunch: Boolean(existingOrder?.has_lunch ?? item.has_lunch),
    lunch_variant_id: existingOrder?.lunch_variant_id ?? item.lunch_variant_id ?? null,
    lunch_count: number(existingOrder?.lunch_count ?? item.lunch_count, 1) || 1,
    lunch_variant_selections: normalizeSelections(
      existingOrder?.lunch_variant_selections || item.lunch_variant_selections,
    ),
    has_dinner: Boolean(existingOrder?.has_dinner ?? item.has_dinner),
    dinner_variant_id: existingOrder?.dinner_variant_id ?? item.dinner_variant_id ?? null,
    dinner_count: number(existingOrder?.dinner_count ?? item.dinner_count, 1) || 1,
    dinner_variant_selections: normalizeSelections(
      existingOrder?.dinner_variant_selections || item.dinner_variant_selections,
    ),
  };
};
